import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Modal,
  Box,
  TextField,
  MenuItem,
  Paper,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Download as DownloadIcon,
  FilterAlt as FilterIcon,
  Close as CloseIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import Chart from "react-google-charts";
import * as XLSX from "xlsx";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "16px",
  maxHeight: "90vh",
  overflowY: "auto",
};

const formatLabel = (key) => {
  const labelMap = {
    totalLodging: "Total Lodging",
    totalBoarding: "Total Boarding",
    totalFood: "Total Food",
    totalConveyance: "Total Conveyance",
    totalRND: "Total R&D",
    totalPO: "Total PO",
    totalInvoice: "Total Invoice",
    totalOther: "Total Other",
    totalExpenses: "Total Expenses",
    lodging: "Lodging",
    boarding: "Boarding",
    food: "Food",
    conveyance: "Conveyance",
    RND: "R&D",
    PO: "PO",
    Invoice: "Invoice",
    other: "Other",
  };

  return (
    labelMap[key] ||
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace("Total ", "")
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

const ExpenseDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedTab, setSelectedTab] = useState(0);
  const [employeeExpenses, setEmployeeExpenses] = useState([]);
  const [filteredEmployeeExpenses, setFilteredEmployeeExpenses] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [totalExpensesByType, setTotalExpensesByType] = useState({});
  const [filteredExpensesByType, setFilteredExpensesByType] = useState({});
  const [expenseTypeFilter, setExpenseTypeFilter] = useState("");
  const [overallTotalExpenses, setOverallTotalExpenses] = useState({});
  const [expensesByCategory, setExpensesByCategory] = useState([]);
  const [filteredExpensesByCategory, setFilteredExpensesByCategory] = useState(
    []
  );
  const [categorySearch, setCategorySearch] = useState("");
  const [expenseRange, setExpenseRange] = useState({ min: "", max: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartTitle, setChartTitle] = useState("");
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const filtered = employeeExpenses.filter((employee) =>
      employee.employeeName.toLowerCase().includes(employeeSearch.toLowerCase())
    );
    setFilteredEmployeeExpenses(filtered);
  }, [employeeSearch, employeeExpenses]);

  useEffect(() => {
    const filtered = expenseTypeFilter
      ? Object.fromEntries(
          Object.entries(totalExpensesByType).filter(([key]) =>
            key.toLowerCase().includes(expenseTypeFilter.toLowerCase())
          )
        )
      : totalExpensesByType;
    setFilteredExpensesByType(filtered);
  }, [expenseTypeFilter, totalExpensesByType]);

  useEffect(() => {
    const filtered = expensesByCategory.filter((category) => {
      const matchesName = category._id
        .toLowerCase()
        .includes(categorySearch.toLowerCase());
      const matchesRange =
        (!expenseRange.min || category.totalExpenses >= expenseRange.min) &&
        (!expenseRange.max || category.totalExpenses <= expenseRange.max);
      return matchesName && matchesRange;
    });
    setFilteredExpensesByCategory(filtered);
  }, [categorySearch, expenseRange, expensesByCategory]);

  const fetchAllData = async () => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        throw new Error(
          "Authentication token is missing. Please log in again."
        );
      }

      setLoading(true);
      setError(null);

      const responses = await Promise.all([
        fetch(`${BASE_URL}/api/reimbursement/expenses/total-by-employee`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BASE_URL}/api/reimbursement/expenses/total-by-type`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BASE_URL}/api/reimbursement/expenses/overall-total`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(
          `${BASE_URL}/api/reimbursement/expenses/expenses-by-employee-category`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
      ]);

      const responseData = await Promise.all(
        responses.map(async (res) => {
          if (!res.ok) {
            if (res.status === 204) {
              return [];
            }
            throw new Error(`Server responded with status ${res.status}`);
          }
          try {
            const text = await res.text();
            return text ? JSON.parse(text) : [];
          } catch (e) {
            return [];
          }
        })
      );

      const allEmpty = responseData.every((data) =>
        Array.isArray(data) ? data.length === 0 : Object.keys(data).length === 0
      );

      if (allEmpty) {
        throw new Error("No expense added yet!");
      }

      setEmployeeExpenses(responseData[0] || []);
      setFilteredEmployeeExpenses(responseData[0] || []);
      setTotalExpensesByType(responseData[1] || {});
      setFilteredExpensesByType(responseData[1] || {});
      setOverallTotalExpenses(responseData[2] || {});
      setExpensesByCategory(responseData[3] || []);
      setFilteredExpensesByCategory(responseData[3] || []);
    } catch (err) {
      if (err.message.includes("No expense added yet!")) {
        setEmployeeExpenses([]);
        setFilteredEmployeeExpenses([]);
        setTotalExpensesByType({});
        setFilteredExpensesByType({});
        setOverallTotalExpenses({});
        setExpensesByCategory([]);
        setFilteredExpensesByCategory([]);
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
  };

  const openChartModal = (data, title) => {
    const hasData = data.length > 1 && data.slice(1).some((row) => row[1] > 0);

    if (!hasData) {
      setChartData([
        ["Category", "Value"],
        ["No Data Available", 1],
      ]);
      setChartTitle(`${title} - No Data Available`);
    } else {
      setChartData(data);
      setChartTitle(title);
    }
    setIsChartModalOpen(true);
  };

  const closeChartModal = () => {
    setIsChartModalOpen(false);
  };

  const exportToExcel = () => {
    const filteredDataWithoutId = filteredExpensesByCategory.map(
      ({ _id, ...rest }) => ({
        Employee: _id,
        Lodging: rest.lodging,
        Boarding: rest.boarding,
        Food: rest.food,
        Conveyance: rest.conveyance,
        "R&D": rest.RND,
        PO: rest.PO,
        Invoice: rest.Invoice,
        Other: rest.other,
        "Total Expenses": rest.totalExpenses,
      })
    );

    const ws = XLSX.utils.json_to_sheet(filteredDataWithoutId);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses by Category");
    XLSX.writeFile(wb, "expenses_by_category.xlsx");
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearFilters = () => {
    setCategorySearch("");
    setExpenseRange({ min: "", max: "" });
  };

  // Shared table styles
  const headerCellSx = {
    bgcolor: "#f5f7fb",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#5a5f7a",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    py: 1.5,
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    fontSize: "0.875rem",
    color: "#1a1a2e",
    py: 2,
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Expense Dashboard"
        subtitle="Comprehensive expense analytics and reporting"
        icon={<AssessmentIcon />}
      />

      {loading ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2, color: "#667eea" }} />
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
              Loading expense data...
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  elevation={0}
                  sx={{ border: "1px solid #eef0f6", borderRadius: "16px", p: 2.5 }}
                >
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={36} sx={{ mt: 1 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : error ? (
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${
              error.includes("No expense added yet") ? "#93C5FD" : "#FCA5A5"
            }`,
            borderRadius: "16px",
            p: 4,
            bgcolor: error.includes("No expense added yet")
              ? "#EFF6FF"
              : "#FEF2F2",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: error.includes("No expense added yet")
                ? "#667eea"
                : "#991B1B",
              fontWeight: 500,
              mb: error.includes("No expense added yet") ? 0 : 2,
            }}
          >
            {error}
          </Typography>
          {!error.includes("No expense added yet") && (
            <Button
              variant="contained"
              onClick={fetchAllData}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
              }}
            >
              Retry
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {/* Overall Stats Row */}
          {Object.keys(overallTotalExpenses).length > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Object.entries(overallTotalExpenses).map(([key, value]) => (
                <Grid item xs={6} sm={4} md={3} key={key}>
                  <StatsCard
                    label={formatLabel(key)}
                    value={formatCurrency(value)}
                    color={key === "totalExpenses" ? "#667eea" : "#059669"}
                    bgColor={key === "totalExpenses" ? "#EBF0FF" : "#D1FAE5"}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Tabs */}
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #eef0f6",
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: "2px solid #f0f0f5",
                px: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "#8b8fa3",
                  "&.Mui-selected": { color: "#667eea" },
                },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  bgcolor: "#667eea",
                },
              }}
            >
              <Tab label="Employee-wise" />
              <Tab label="By Type" />
              <Tab label="Overall" />
              <Tab label="By Category" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Tab 0: Employee-wise */}
              {selectedTab === 0 && (
                <Box>
                  <TextField
                    placeholder="Search employees..."
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { borderRadius: "10px" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#8b8fa3" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {filteredEmployeeExpenses.length === 0 ? (
                    <EmptyState
                      title={
                        employeeSearch
                          ? `No employees found matching "${employeeSearch}"`
                          : "No employee data available"
                      }
                      subtitle="Try adjusting your search criteria."
                      action={
                        employeeSearch && (
                          <Button
                            onClick={() => setEmployeeSearch("")}
                            sx={{
                              textTransform: "none",
                              color: "#667eea",
                              fontWeight: 600,
                            }}
                          >
                            Clear search
                          </Button>
                        )
                      }
                    />
                  ) : (
                    <>
                      <TableContainer
                        sx={{
                          maxHeight: 500,
                          mb: 3,
                          border: "1px solid #eef0f6",
                          borderRadius: "10px",
                          overflowX: "auto",
                          maxWidth: "100%",
                        }}
                      >
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerCellSx}>Employee Name</TableCell>
                              <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                                Total Expenses
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredEmployeeExpenses.map((employee) => (
                              <TableRow key={employee._id} hover>
                                <TableCell sx={bodyCellSx}>
                                  {employee.employeeName}
                                </TableCell>
                                <TableCell
                                  sx={{ ...bodyCellSx, textAlign: "right", fontWeight: 600 }}
                                >
                                  {formatCurrency(employee.totalExpenses)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          variant="outlined"
                          startIcon={<BarChartIcon />}
                          onClick={() =>
                            openChartModal(
                              [
                                ["Employee", "Expenses"],
                                ...filteredEmployeeExpenses.map((e) => [
                                  e.employeeName,
                                  e.totalExpenses,
                                ]),
                              ],
                              "Employee-wise Expenses"
                            )
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "10px",
                            borderColor: "#667eea",
                            color: "#667eea",
                            "&:hover": { borderColor: "#5a6fd8", bgcolor: "#eef0ff" },
                          }}
                        >
                          View Chart
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Tab 1: By Type */}
              {selectedTab === 1 && (
                <Box>
                  <TextField
                    select
                    label="Filter by Expense Type"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={expenseTypeFilter}
                    onChange={(e) => setExpenseTypeFilter(e.target.value)}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { borderRadius: "10px" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterIcon sx={{ color: "#8b8fa3" }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {Object.keys(totalExpensesByType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {formatLabel(type)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {Object.entries(filteredExpensesByType).map(([key, value]) => (
                      <Grid item xs={6} sm={4} md={3} key={key}>
                        <StatsCard
                          label={formatLabel(key)}
                          value={formatCurrency(value)}
                          color="#667eea"
                          bgColor="#EBF0FF"
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="outlined"
                      startIcon={<PieChartIcon />}
                      onClick={() =>
                        openChartModal(
                          [
                            ["Expense Type", "Amount"],
                            ...Object.entries(filteredExpensesByType).map(
                              ([type, amount]) => [formatLabel(type), amount]
                            ),
                          ],
                          "Expenses by Type"
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "10px",
                        borderColor: "#667eea",
                        color: "#667eea",
                        "&:hover": { borderColor: "#5a6fd8", bgcolor: "#eef0ff" },
                      }}
                    >
                      View Chart
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Tab 2: Overall */}
              {selectedTab === 2 && (
                <Box>
                  <Grid container spacing={2}>
                    {Object.entries(overallTotalExpenses).map(([key, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: "16px",
                            border: "1px solid #eef0f6",
                            bgcolor:
                              key === "totalExpenses" ? "#667eea" : "#059669",
                            color: "#fff",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              opacity: 0.85,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            {formatLabel(key)}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
                            {formatCurrency(value)}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Tab 3: By Category */}
              {selectedTab === 3 && (
                <Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FilterIcon />}
                      onClick={toggleFilters}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                        borderColor: "#CBD5E1",
                        color: "#5a5f7a",
                      }}
                    >
                      {showFilters ? "Hide Filters" : "Filters"}
                    </Button>
                    {showFilters && (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={clearFilters}
                        sx={{
                          textTransform: "none",
                          color: "#EF4444",
                          fontWeight: 600,
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>

                  {showFilters && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: "10px",
                        border: "1px solid #eef0f6",
                        bgcolor: "#f5f7fb",
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            placeholder="Search employee..."
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            sx={{ "& .MuiInputBase-root": { borderRadius: "8px" } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: "#8b8fa3" }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Min Amount"
                            variant="outlined"
                            type="number"
                            fullWidth
                            size="small"
                            value={expenseRange.min}
                            onChange={(e) =>
                              setExpenseRange({ ...expenseRange, min: e.target.value })
                            }
                            sx={{ "& .MuiInputBase-root": { borderRadius: "8px" } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">{"\u20B9"}</InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Max Amount"
                            variant="outlined"
                            type="number"
                            fullWidth
                            size="small"
                            value={expenseRange.max}
                            onChange={(e) =>
                              setExpenseRange({ ...expenseRange, max: e.target.value })
                            }
                            sx={{ "& .MuiInputBase-root": { borderRadius: "8px" } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">{"\u20B9"}</InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  <TableContainer
                    sx={{
                      maxHeight: 500,
                      mb: 3,
                      border: "1px solid #eef0f6",
                      borderRadius: "10px",
                      overflowX: "auto",
                      maxWidth: "100%",
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Employee</TableCell>
                          {!isMobile && (
                            <>
                              <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                                Lodging
                              </TableCell>
                              <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                                Boarding
                              </TableCell>
                              <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                                Food
                              </TableCell>
                              <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                                Conveyance
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ ...headerCellSx, textAlign: "center", width: 60 }}>
                            Chart
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredExpensesByCategory.map((category) => (
                          <TableRow key={category.employeeId} hover>
                            <TableCell sx={bodyCellSx}>{category._id}</TableCell>
                            {!isMobile && (
                              <>
                                <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                                  {formatCurrency(category.lodging)}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                                  {formatCurrency(category.boarding)}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                                  {formatCurrency(category.food)}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                                  {formatCurrency(category.conveyance)}
                                </TableCell>
                              </>
                            )}
                            <TableCell
                              sx={{
                                ...bodyCellSx,
                                textAlign: "right",
                                fontWeight: 700,
                                color: "#1a1a2e",
                              }}
                            >
                              {formatCurrency(category.totalExpenses)}
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openChartModal(
                                    [
                                      ["Category", "Expense"],
                                      ["Lodging", category.lodging],
                                      ["Boarding", category.boarding],
                                      ["Food", category.food],
                                      ["Conveyance", category.conveyance],
                                      ["R&D", category.RND],
                                      ["PO", category.PO],
                                      ["Invoice", category.Invoice],
                                      ["Other", category.other],
                                    ],
                                    `Expense Breakdown for ${category._id}`
                                  )
                                }
                                sx={{
                                  color: "#667eea",
                                  "&:hover": { bgcolor: "#eef0ff" },
                                }}
                              >
                                <PieChartIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={exportToExcel}
                      sx={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "10px",
                        "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
                      }}
                    >
                      Download Excel
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<BarChartIcon />}
                      onClick={() =>
                        openChartModal(
                          [
                            [
                              "Category",
                              "Lodging",
                              "Boarding",
                              "Food",
                              "Conveyance",
                              "R&D",
                              "PO",
                              "Invoice",
                              "Other",
                            ],
                            ...expensesByCategory.map((c) => [
                              c._id,
                              c.lodging,
                              c.boarding,
                              c.food,
                              c.conveyance,
                              c.RND,
                              c.PO,
                              c.Invoice,
                              c.other,
                            ]),
                          ],
                          "Expenses by Category"
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "10px",
                        borderColor: "#667eea",
                        color: "#667eea",
                        "&:hover": { borderColor: "#5a6fd8", bgcolor: "#eef0ff" },
                      }}
                    >
                      View Chart
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Chart Modal */}
      <Modal
        open={isChartModalOpen}
        onClose={closeChartModal}
        aria-labelledby="chart-modal-title"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              pb: 2,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            <Typography
              id="chart-modal-title"
              sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e" }}
            >
              {chartTitle}
            </Typography>
            <IconButton
              onClick={closeChartModal}
              size="small"
              sx={{
                color: "#8b8fa3",
                "&:hover": { color: "#EF4444", bgcolor: "#FEE2E2" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {chartTitle.includes("No Data Available") ? (
            <EmptyState
              title="No data available"
              subtitle="Please try a different filter or category."
              sx={{ py: 6 }}
            />
          ) : (
            <Chart
              chartType={
                chartData[0] && chartData[0].length > 2
                  ? "BarChart"
                  : "PieChart"
              }
              data={chartData}
              width="100%"
              height="400px"
              options={{
                colors: [
                  "#667eea",
                  "#059669",
                  "#F59E0B",
                  "#EF4444",
                  "#7C3AED",
                  "#F97316",
                  "#0891B2",
                  "#78716C",
                ],
                chartArea: { width: "80%", height: "70%" },
                legend: { position: "right" },
                animation: {
                  startup: true,
                  easing: "linear",
                  duration: 1500,
                },
                hAxis: { title: "Amount", minValue: 0 },
                vAxis: { title: "Category" },
                pieSliceText: "value-and-percentage",
                pieHole: 0.4,
                is3D: false,
                backgroundColor: "transparent",
              }}
            />
          )}
        </Box>
      </Modal>
    </PageContainer>
  );
};

export default ExpenseDashboard;
